import { useState, useEffect } from "react";
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import PageForm from '../../templates/PageForm';
import CopyPasteTable, { HeaderTable } from '../../organisms/CopyPasteTable';
import { v4 as uuidv4 } from 'uuid';
import { FaPlay, FaPlayCircle, FaCheck, FaTimes, FaSpinner } from 'react-icons/fa';
import ActionButton from "../../molecules/ActionButton";
import { currencyToNumber } from '../../../utils/currencyUtils';
import { formatDateFromBRToISO } from '../../../utils/DateUtils';

const BASE_URL = process.env.REACT_APP_BILLER_API_URL + '/bills';
const API_KEY = process.env.REACT_APP_API_KEY || '';

interface BillRow {
    data: string[];
    status: 'pending' | 'sending' | 'success' | 'error';
    message?: string;
    id?: string;
}

const BillerBatchCreateBill = () => {
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(false);
    const [billRows, setBillRows] = useState<BillRow[]>([]);
    const [tableData, setTableData] = useState<string[][]>([]);

    useEffect(() => {
        const handleJsonGenerated = (event: Event) => {
            const data = (event as CustomEvent).detail as Record<string, string>[];
            const rows = data.map(row => Object.values(row));
            setTableData(rows);
            setBillRows(rows.map(row => ({
                data: row,
                status: 'pending' as const
            })));
        };

        window.addEventListener("CopyPasteTable:jsonGenerated", handleJsonGenerated);

        return () => {
            window.removeEventListener("CopyPasteTable:jsonGenerated", handleJsonGenerated);
        };
    }, []);

    // Headers baseados nos campos obrigatórios do schema
    const headers: HeaderTable[] = [
        { label: 'Data Emissão', field: 'data_emissao', required: true, format: 'date' },
        { label: 'Data Vencimento', field: 'data_vencimento', required: true, format: 'date' },
        { label: 'Valor Título', field: 'valor_titulo', required: true, format: 'currency' },
        { label: 'Título', field: 'titulo', required: true, format: 'number', size: 8 },
        { label: 'Nome Pagador', field: 'pagador_nome_pessoa', required: true, format: 'text' },
        { label: 'Documento', field: 'pagador_documento', required: true, format: 'text' },
        { label: 'Logradouro', field: 'pagador_nome_logradouro', required: true, format: 'text' },
        { label: 'Bairro', field: 'pagador_nome_bairro', required: true, format: 'text' },
        { label: 'Cidade', field: 'pagador_nome_cidade', required: true, format: 'text' },
        { label: 'UF', field: 'pagador_sigla_UF', required: true, format: 'text' },
        { label: 'CEP', field: 'pagador_numero_CEP', required: true, format: 'text' }
    ];

    const buildPayloadFromRow = (row: string[]): any => {
        return {
            id: uuidv4(),
            data_emissao: formatDateFromBRToISO(row[0]),
            data_vencimento: formatDateFromBRToISO(row[1]),
            valor_titulo: currencyToNumber(row[2]),
            titulo: row[3],
            pagador: {
                nome_pessoa: row[4],
                documento: row[5],
                nome_logradouro: row[6],
                nome_bairro: row[7],
                nome_cidade: row[8],
                sigla_UF: row[9],
                numero_CEP: row[10]
            }
        };
    };

    const sendSingleBill = async (rowIndex: number): Promise<void> => {
        const row = billRows[rowIndex];
        if (!row || row.data.length === 0) return;

        // Atualiza status para "sending"
        setBillRows(prev => prev.map((r, i) =>
            i === rowIndex ? { ...r, status: 'sending' as const } : r
        ));

        try {
            const payload = buildPayloadFromRow(row.data);

            const response = await fetch(BASE_URL, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'x-api-key': API_KEY
                },
                body: JSON.stringify(payload)
            });

            if (response.status === 201) {
                setBillRows(prev => prev.map((r, i) =>
                    i === rowIndex ? {
                        ...r,
                        status: 'success' as const,
                        message: 'Boleto criado com sucesso',
                        id: payload.id
                    } : r
                ));
            } else {
                const error = await response.json();
                setBillRows(prev => prev.map((r, i) =>
                    i === rowIndex ? {
                        ...r,
                        status: 'error' as const,
                        message: error.message || `Erro ${response.status}`
                    } : r
                ));
            }
        } catch (error: any) {
            setBillRows(prev => prev.map((r, i) =>
                i === rowIndex ? {
                    ...r,
                    status: 'error' as const,
                    message: error.message || 'Erro de conexão'
                } : r
            ));
        }
    };

    const sendAllBills = async (): Promise<void> => {
        setIsLoading(true);

        for (let i = 0; i < billRows.length; i++) {
            if (billRows[i].status === 'pending') {
                await sendSingleBill(i);
                // Pequena pausa entre requisições
                await new Promise(resolve => setTimeout(resolve, 100));
            }
        }

        setIsLoading(false);
        toast.info('Processamento em lote concluído');
    };

    // const handleTableDataChange = useCallback((data: string[][]) => {
    //     setTableData(data);
    //     // Converte dados da tabela para billRows
    //     const newBillRows: BillRow[] = data
    //         .filter(row => row.some(cell => cell.trim() !== ''))
    //         .map(row => ({
    //             data: row,
    //             status: 'pending' as const
    //         }));
    //     setBillRows(newBillRows);
    //     console.log('Tabela atualizada:', newBillRows);
    // }, []);

    const getStatusIcon = (status: BillRow['status']) => {
        switch (status) {
            case 'pending':
                return <FaPlay className="text-blue-500" />;
            case 'sending':
                return <FaSpinner className="text-yellow-500 animate-spin" />;
            case 'success':
                return <FaCheck className="text-green-500" />;
            case 'error':
                return <FaTimes className="text-red-500" />;
        }
    };

    const getStatusColor = (status: BillRow['status']) => {
        switch (status) {
            case 'pending':
                return 'bg-blue-50 border-blue-200';
            case 'sending':
                return 'bg-yellow-50 border-yellow-200';
            case 'success':
                return 'bg-green-50 border-green-200';
            case 'error':
                return 'bg-red-50 border-red-200';
        }
    };

    const pendingCount = billRows.filter(r => r.status === 'pending').length;
    const successCount = billRows.filter(r => r.status === 'success').length;
    const errorCount = billRows.filter(r => r.status === 'error').length;

    return (
        <PageForm
            pageTitle="Criar Boletos em Lote"
            isLoading={isLoading}
            apiVersion=""
            breadcrumb={[
                { label: 'Financeiro', url: '/financial' },
                { label: 'Lista de Boletos', url: '/financial/biller' },
                { label: 'Criar Lote', url: '#' }
            ]}
            controlSlot={
                <div className="pt-4 flex justify-between items-center">
                    <div className="flex gap-4 text-sm">
                        <span className="text-blue-600">Pendentes: {pendingCount}</span>
                        <span className="text-green-600">Sucessos: {successCount}</span>
                        <span className="text-red-600">Erros: {errorCount}</span>
                    </div>
                    <div className="flex gap-2">
                        <ActionButton
                            label="Boleto Individual"
                            className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600 disabled:opacity-50"
                            onClick={() => navigate('/financial/biller/create')}
                        />
                        <button
                            onClick={sendAllBills}
                            disabled={isLoading || pendingCount === 0}
                            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
                        >
                            <FaPlayCircle />
                            Enviar Todos ({pendingCount})
                        </button>
                    </div>
                </div>
            }
        >
            <div className="space-y-6">
                {/* Instruções */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-2">
                    <h3 className="text-lg font-medium text-blue-900 mb-2">Como usar:</h3>
                    <ul className="text-sm text-blue-800 space-y-1">
                        <li>• Cole os dados da tabela diretamente na campo de texto</li>
                        <li>• Use o botão "Enviar" em cada linha para testar individualmente</li>
                        <li>• Use "Enviar Todos" para processar todas as linhas pendentes</li>
                    </ul>
                </div>

                {/* Tabela de Input */}
                <div className="bg-white rounded-lg shadow">
                    <div className="p-4 border-b">
                        <h3 className="text-lg font-medium">Dados dos Boletos</h3>
                    </div>
                    <div className="p-4">
                        <CopyPasteTable
                            fixedHeaders={headers}
                            allowDuplicates={false}
                        />
                    </div>
                </div>

                {/* Lista de Status */}
                {billRows.length > 0 && (
                    <div className="bg-white rounded-lg shadow">
                        <div className="p-4 border-b">
                            <h3 className="text-lg font-medium">Status dos Envios</h3>
                        </div>
                        <div className="p-4">
                            <div className="space-y-2 max-h-96 overflow-y-auto">
                                {billRows.map((row, index) => (
                                    <div
                                        key={index}
                                        className={`p-3 rounded border ${getStatusColor(row.status)} flex items-center justify-between`}
                                    >
                                        <div className="flex items-center gap-3">
                                            {getStatusIcon(row.status)}
                                            <div>
                                                <div className="font-medium text-sm">
                                                    Linha {index + 1}: {row.data[4]} - Título: {row.data[3]}
                                                </div>
                                                {row.message && (
                                                    <div className="text-xs text-gray-600 mt-1">
                                                        {row.message}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            {row.status === 'pending' && (
                                                <button
                                                    onClick={() => sendSingleBill(index)}
                                                    className="px-3 py-1 bg-blue-500 text-white text-xs rounded hover:bg-blue-600"
                                                >
                                                    Enviar
                                                </button>
                                            )}
                                            {row.id && (
                                                <span className="text-xs text-gray-500">
                                                    ID: {row.id}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </PageForm>
    );
};

export default BillerBatchCreateBill;
